import { buildApiUrl } from "../../config/api";
import React, { useState, useEffect } from "react";
import axios from "axios";
import "./ProductosAdmin.css";
import { Pencil, Plus, Sheet, ToggleLeft, ToggleRight } from "lucide-react";
import Permiso from "../../router/Permiso";
import { useNavigate } from "react-router-dom";
import { ProductService } from "../../services/ProductService";
import { toast } from "react-toastify";

const ListadoProductos = ({ token, debeRecargar, onRecargaCompleta }) => {
  const navigate = useNavigate();

  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [buscarProducto, setBuscarProducto] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);

  useEffect(() => {
    fetchProductos();
  }, [currentPage, buscarProducto]);

  const fetchProductos = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await ProductService.getProducts({
        token,
        pagina: currentPage,
        pageSize: 20,
        nombre: buscarProducto,
      });

      setProductos(res.productos);
      setTotalPages(res.totalPaginas);
      setTotalRegistros(res.totalRegistros);
    } catch (err) {
      setError("Error al cargar los productos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debeRecargar) {
      fetchProductos().then(() => {
        onRecargaCompleta(); // ← marca como recargado
      });
    }
  }, [debeRecargar]);

  const handleToggleEstado = async (producto) => {
    try {
      const nuevoEstado = await ProductService.toggleEstadoProducto({ token, producto });

      fetchProductos(); // Recargar la lista
      toast.success(`Producto ${nuevoEstado === "ACTIVO" ? "activado" : "desactivado"} exitosamente`);
    } catch (err) {
      toast.error("Error al cambiar el estado del producto");
      console.error("Error:", err);
    }
  };

  const exportarExcel = () => {
    // Crear contenido CSV para exportar
    const headers = [
      "ID",
      "Nombre",
      "Categoría",
      "Descripción",
      "Precio",
      "Estado",
    ];
    const csvContent = [
      headers.join(","),
      ...productos.map((p) =>
        [
          p.id,
          p.nombre || "N/A",
          p.categoria_nombre,
          p.descripcion,
          p.precio,
          p.estado,
        ].join(",")
      ),
    ].join("\n");

    // Crear y descargar archivo
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "productos_export.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getEstadoClass = (estado) => {
    return estado === "ACTIVO" ? "estado-activo" : "estado-baja";
  };

  const handleUpdateProduct = async (producto) => {
    navigate(`/admin/productos/${producto.id}`);
  };

  return (
    <div className="productos-admin-container">
      <div className="productos-header">
        <h2> Lista de Productos</h2>
        <div className="productos-actions">
          <Permiso permiso="export_products">
            <button onClick={exportarExcel} className="btn-exportar">
              <Sheet size={18} style={{ marginRight: "8px" }} />
              Exportar a Excel
            </button>
          </Permiso>
        </div>
      </div>

      {/* Filtro de búsqueda */}
      <div className="usuarios-filtros">
        <div className="filtro-item">
          <label>Buscar:</label>
          <input
            type="text"
            value={buscarProducto}
            onChange={(e) => setBuscarProducto(e.target.value)}
            placeholder="Ingrese nombre o código del producto"
            className=""
          />
        </div>
        <div className="filtro-grupo">
          <button onClick={fetchProductos} className="btn-buscar">
            Buscar
          </button>
        </div>
        <div className="filtro-grupo">
          <button
            onClick={() => {
              setBuscarProducto("");
            }}
            className="btn-limpiar-filtros"
          >
            Limpiar Filtros
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Cargando productos...</p>
        </div>
      ) : (
        <>
          <div className="productos-table-wrapper">
            <table className="productos-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Código</th>
                  <th>Nombre del Producto</th>
                  <th>Categoría</th>
                  <th>Descripción</th>
                  <th>Precio</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {productos.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="no-data">
                      <div className="no-data-content">
                        <span>📦</span>
                        <p>No hay productos registrados</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  productos.map((producto) => (
                    <tr key={producto.id} className="producto-row">
                      <td className="producto-id">#{producto.id}</td>
                      <td className="">
                        {producto.codigo}
                      </td>
                      <td className="">{producto.nombre || "Sin nombre"}</td>
                      <td className="">
                        <span className="categoria-badge">
                          {producto.categoria_nombre}
                        </span>
                      </td>
                      <td className="producto-descripcion">
                        {producto.descripcion}
                      </td>
                      <td className="">
                        S/ {parseFloat(producto.precio).toFixed(2)}
                      </td>
                      <td className="">
                        <span
                          className={`estado-producto ${getEstadoClass(
                            producto.estado
                          )}`}
                        >
                          {producto.estado}
                        </span>
                      </td>
                      <td className="producto-acciones">
                        <Permiso permiso="edit_product">
                          <span
                            style={{ cursor: "pointer" }}
                            onClick={() => handleUpdateProduct(producto)}
                          >
                            <Pencil size={20} />
                          </span>
                        </Permiso>
                        <Permiso permiso="delete_products">
                          <button
                            onClick={() => handleToggleEstado(producto)}
                            className={`btn-toggle ${producto.estado == "ACTIVO"
                              ? "btn-apagar"
                              : "btn-encender"
                              }`}
                            title={
                              producto.estado === "ACTIVO"
                                ? "Desactivar producto"
                                : "Activar producto"
                            }
                          >
                            {producto.estado == "ACTIVO" ? (
                              <ToggleLeft size={22} color="#e74c3c" />
                            ) : (
                              <ToggleRight size={22} color="#2ecc71" />
                            )}
                          </button>
                        </Permiso>
                      </td>
                    </tr>
                  ))
                )} 
              </tbody>
            </table>
          </div>
          <span style={{ marginLeft: "12px", fontSize: "14px", color: "#555" }}>
            {" "}
            Total de registros: {totalRegistros}{" "}
          </span>
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <span>
              Página {currentPage} de {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Siguiente
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ListadoProductos;
